<template>
    <div :id="`${serviceNameResolver()}-parent-section`" class="w-full xl:h-[615px] md:h-[500px] h-[518px] relative">
        <NuxtImg
            placeholder
            loading="lazy"
            provider="imgProvider"
            width="100"
            height="615"
            class="size-full object-cover"
            :src="`/Landings/${serviceSlug}/${serviceSlug}-upload.webp`"
            alt="upload photo background"
        />

        <div class="text-center absolute top-1/2 w-full -translate-y-1/2 left-1/2 -translate-x-1/2">
            <p class="md:text-title-lg text-title-md text-gray-100 font-bold mb-2">
                {{ $t(`landingComponents_uploader_${serviceSlug.toUpperCase()}_title`) }}
            </p>
            <p class="md:text-body-md text-body-sm text-gray-400 mb-6">
                {{ $t(`landingComponents_uploader_${serviceSlug.toUpperCase()}_subtitle_test`) }}
            </p>

            <FileUploader
                :id="handleId + '-uploader'"
                :state="uploadState"
                @on-drop="handleUploaderChange"
                @on-change="handleUploaderChange"
                :class="{ '!bg-white/10': uploadState !== 'error' }"
                class="backdrop-blur-lg !rounded-3xl xl:!h-[350px] md:!h-[260px] !h-[263px] md:!w-[585px] !w-[343px]"
            >
                <FileUploaderDefaultSlot v-if="!isFetching && !error" />
                <FileUploaderInprogress v-if="isFetching" />
                <FileUploaderError
                    v-if="error"
                    :error
                    @reset="handleResetError"
                    class="my-auto flex flex-col justify-center items-center h-full"
                />
            </FileUploader>
        </div>
    </div>
</template>

<script setup lang="ts">
import { useFetch } from "@vueuse/core";

interface IProps {
    serviceSlug: "vs" | "ir" | "ie" | "dd";
}

const props = defineProps<IProps>();

const uploadState = ref<"default" | "error" | "inprogress">("default");

const uploadedFile = ref<File>();

const baseUrl = `${useRuntimeConfig().public.baseURL}/order/image`;
const { openSnackbar } = useSnackbar();

const localePath = useLocalePath();

const { execute, isFetching, error } = useFetch(baseUrl, {
    immediate: false,
    updateDataOnError: true,

    beforeFetch: (ctx) => {
        uploadState.value = "default";
        const fd = new FormData();
        fd.append("image", uploadedFile.value as any);
        if (props.serviceSlug !== "ir") fd.append("service_name", serviceNameResolver());
        ctx.options.body = fd;
        ctx.options.method = "POST";
    },

    afterFetch(ctx) {
        const dataObject = JSON.parse(ctx.data);
        if (dataObject.order_id) {
            useRouter().push(localePath(`/dashboard/generate/${dataObject.order_id}`));
        } else {
            openSnackbar({
                state: "error",
                description: "something went wrong. Please try again.",
            });
        }
        return ctx;
    },

    onFetchError(ctx) {
        uploadState.value = "error";
        const dataObject = JSON.parse(ctx.data)?.error ?? "something went wrong";
        return { error: dataObject };
    },
});

watch(isFetching, () => {
    if (isFetching.value) uploadState.value = "inprogress";
});

function serviceNameResolver() {
    switch (props.serviceSlug) {
        case "vs":
            return "service-ai-virtual-staging";
        case "ie":
            return "service-image-enhancement";
        case "ir":
            return "service-item-removal";
        case "dd":
            return "service-day-to-dusk";
        default:
            return "";
    }
}

function handleUploaderChange(file: FileList) {
    handleSizeValidation(file);
}

function handleSizeValidation(file: FileList) {
    uploadedFile.value = file[0];
    if (uploadedFile.value && uploadedFile.value.size < 50000000) {
        execute();
    } else {
        uploadState.value = "error";
        error.value = "Uploaded Image Must be at last 50MBs";
    }
}

function handleResetError() {
    error.value = null;
    uploadState.value = "default";
}
const handleId = computed(() => {
    if (useRoute().fullPath.includes("virtual-staging")) return "virtualstaging";
    if (useRoute().fullPath.includes("image-enhancement")) return "imageenhancement";
    if (useRoute().fullPath.includes("day-to-dusk")) return "daytodusk";
    if (useRoute().fullPath.includes("item-removal")) return "itemremoval";
});
</script>
